import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Badge } from 'react-bootstrap';
import { FaCalendarAlt, FaUsers, FaChartLine, FaDownload } from 'react-icons/fa';
import apiClient from '../apiClient';
import './CommunicationDashboard.css';

function CommunicationDashboard() {
  const [activeTab, setActiveTab] = useState('timeline');
  const [communications, setCommunications] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    scheduled: 0,
    draft: 0,
    bySource: {
      manual: 0,
      event: 0,
      plan: 0,
      assessment: 0
    }
  });
  
  // Filters
  const [filters, setFilters] = useState({
    project: '',
    stakeholder: '',
    type: '',
    source: '',
    dateFrom: '',
    dateTo: '',
    status: ''
  });
  
  
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all communications
      const [commsResponse, stakeholdersResponse, projectsResponse] = await Promise.all([
        apiClient.get('/communications/all'),
        apiClient.get('/v2/stakeholders'), // Use v2 endpoint
        apiClient.get('/projects')
      ]);
      
      // Check if commsResponse.data is an array or has communications property
      // Handle different response formats
      let commsData = [];
      if (Array.isArray(commsResponse.data)) {
        commsData = commsResponse.data;
      } else if (commsResponse.data && typeof commsResponse.data === 'object') {
        if (commsResponse.data.communications) {
          commsData = commsResponse.data.communications;
        } else if (commsResponse.data.data) {
          // Sometimes APIs nest data in data.data
          commsData = commsResponse.data.data;
        }
      }
      
      setCommunications(commsData);
      setStakeholders(stakeholdersResponse.data.stakeholders || []);
      setProjects(projectsResponse.data || []); // Projects endpoint returns array directly
      
      // Calculate statistics
      calculateStats(commsData);
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load communication data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (comms) => {
    const stats = {
      total: comms.length,
      sent: comms.filter(c => c.status === 'sent' || c.outcome === 'sent').length,
      scheduled: comms.filter(c => c.status === 'scheduled' || c.outcome === 'scheduled').length,
      draft: comms.filter(c => c.status === 'draft' || c.outcome === 'draft').length,
      bySource: {
        manual: comms.filter(c => c.source === 'manual').length,
        event: comms.filter(c => c.source === 'event').length,
        plan: comms.filter(c => c.source === 'plan').length,
        assessment: comms.filter(c => c.source === 'assessment').length
      }
    };
    setStats(stats);
  };

  const getFilteredCommunications = () => {
    return communications.filter(comm => {
      // Apply filters
      const matchesProject = !filters.project || comm.project_id === parseInt(filters.project);
      const matchesStakeholder = !filters.stakeholder || comm.stakeholder_id === parseInt(filters.stakeholder);
      const matchesType = !filters.type || comm.communication_type === filters.type;
      const matchesSource = !filters.source || comm.source === filters.source;
      const matchesStatus = !filters.status || comm.status === filters.status || comm.outcome === filters.status;
      
      // Date filtering
      const commDate = new Date(comm.communication_date || comm.created_at);
      const matchesDateFrom = !filters.dateFrom || commDate >= new Date(filters.dateFrom);
      const matchesDateTo = !filters.dateTo || commDate <= new Date(filters.dateTo);
      
      // Search
      const matchesSearch = !searchTerm || 
        comm.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comm.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comm.stakeholder?.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesProject && matchesStakeholder && matchesType && matchesSource && 
             matchesStatus && matchesDateFrom && matchesDateTo && matchesSearch;
    });
  };

  const exportToCSV = () => {
    const filteredComms = getFilteredCommunications();
    const headers = ['Date', 'Stakeholder', 'Project', 'Type', 'Subject', 'Status', 'Source'];
    const rows = filteredComms.map(comm => [
      new Date(comm.communication_date || comm.created_at).toLocaleDateString(),
      comm.stakeholder?.full_name || '',
      comm.project?.name || '',
      comm.communication_type,
      comm.subject,
      comm.status || comm.outcome || 'sent',
      comm.source || 'manual'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `communications_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Temporarily comment out unused function to fix ESLint warning
  // const getSourceIcon = (source) => {
  //   switch (source) {
  //     case 'event': return <FaProjectDiagram className="text-primary" />;
  //     case 'plan': return <FaProjectDiagram className="text-success" />;
  //     case 'assessment': return <FaFileAlt className="text-warning" />;
  //     case 'manual': 
  //     default: 
  //       return <FaEnvelope className="text-secondary" />;
  //   }
  // };

  const getStatusBadge = (comm) => {
    const status = comm.status || comm.outcome || 'sent';
    const variants = {
      'sent': 'success',
      'scheduled': 'warning',
      'draft': 'secondary',
      'failed': 'danger',
      'delivered': 'info',
      'read': 'primary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const renderTimelineView = () => {
    const filteredComms = getFilteredCommunications();
    
    // Group communications by date
    const groupedByDate = {};
    filteredComms.forEach(comm => {
      const date = new Date(comm.communication_date || comm.created_at).toLocaleDateString();
      if (!groupedByDate[date]) {
        groupedByDate[date] = [];
      }
      groupedByDate[date].push(comm);
    });
    
    // Sort dates in descending order (most recent first)
    const sortedDates = Object.keys(groupedByDate).sort((a, b) => 
      new Date(b) - new Date(a)
    );
    
    if (filteredComms.length === 0) {
      return (
        <div className="text-center p-5">
          <p className="text-muted">No communications found</p>
        </div>
      );
    }
    
    return (
      <div className="timeline-view">
        {sortedDates.map(date => (
          <div key={date} className="timeline-date-group">
            <h5 className="timeline-date">
              <FaCalendarAlt className="me-2" />
              {date}
            </h5>
            {groupedByDate[date].map(comm => (
              <Card key={comm.id} className="timeline-item mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{comm.subject || 'No Subject'}</h6>
                      <p className="text-muted mb-2">
                        <strong>To:</strong> {comm.stakeholder?.full_name || 'Unknown'} • 
                        <strong>Type:</strong> {comm.communication_type} • 
                        <strong>Project:</strong> {comm.project?.name || 'N/A'}
                      </p>
                      {comm.content && (
                        <p className="comm-preview">{comm.content.substring(0, 150)}...</p>
                      )}
                    </div>
                    <div className="ms-3">
                      {getStatusBadge(comm)}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderStakeholderView = () => {
    const filteredComms = getFilteredCommunications();
    const groupedByStakeholder = {};
    
    filteredComms.forEach(comm => {
      const stakeholderId = comm.stakeholder_id;
      if (!groupedByStakeholder[stakeholderId]) {
        groupedByStakeholder[stakeholderId] = {
          stakeholder: comm.stakeholder,
          communications: []
        };
      }
      groupedByStakeholder[stakeholderId].communications.push(comm);
    });

    return (
      <div className="stakeholder-view">
        {Object.values(groupedByStakeholder).map(({ stakeholder, communications }) => (
          <Card key={stakeholder?.id} className="mb-3">
            <Card.Header>
              <h5 className="mb-0">
                <FaUsers /> {stakeholder?.full_name}
                <Badge bg="primary" className="ms-2">{communications.length}</Badge>
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="comm-grid">
                {communications.slice(0, 5).map(comm => (
                  <div key={comm.id} className="comm-item">
                    <strong>{comm.subject}</strong>
                    <span className="text-muted ms-2">
                      {new Date(comm.communication_date || comm.created_at).toLocaleDateString()}
                    </span>
                    {getStatusBadge(comm)}
                  </div>
                ))}
                {communications.length > 5 && (
                  <p className="text-muted">...and {communications.length - 5} more</p>
                )}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  };

  if (loading) return <div className="text-center p-5">Loading communication data...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <Container fluid className="communication-dashboard">
      <Row className="mb-4">
        <Col>
          <h1>Communication Dashboard</h1>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <h3>{stats.total}</h3>
              <p>Total Communications</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <h3>{stats.sent}</h3>
              <p>Sent</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <h3>{stats.scheduled}</h3>
              <p>Scheduled</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <h3>{stats.draft}</h3>
              <p>Drafts</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={2}>
              <Form.Select 
                value={filters.project} 
                onChange={(e) => setFilters({...filters, project: e.target.value})}
              >
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select 
                value={filters.stakeholder} 
                onChange={(e) => setFilters({...filters, stakeholder: e.target.value})}
              >
                <option value="">All Stakeholders</option>
                {stakeholders.map(s => (
                  <option key={s.id} value={s.id}>{s.full_name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select 
                value={filters.type} 
                onChange={(e) => setFilters({...filters, type: e.target.value})}
              >
                <option value="">All Types</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="meeting">Meeting</option>
                <option value="newsletter">Newsletter</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select 
                value={filters.source} 
                onChange={(e) => setFilters({...filters, source: e.target.value})}
              >
                <option value="">All Sources</option>
                <option value="manual">Manual</option>
                <option value="event">Event</option>
                <option value="plan">Plan</option>
                <option value="assessment">Assessment</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Button variant="secondary" onClick={exportToCSV}>
                <FaDownload /> Export
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Navigation Tabs - using same pattern as Training.js */}
      <div style={{ background: 'white', borderBottom: '2px solid #e9ecef', marginBottom: '20px' }}>
        <div style={{ display: 'flex', paddingLeft: '20px' }}>
          <button
            onClick={() => setActiveTab('timeline')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'timeline' ? '#007bff' : 'transparent',
              color: activeTab === 'timeline' ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'timeline' ? '600' : '400',
              borderRadius: activeTab === 'timeline' ? '4px 4px 0 0' : '0',
              marginRight: '4px',
              transition: 'all 0.2s',
              borderBottom: activeTab === 'timeline' ? '2px solid #007bff' : '2px solid transparent'
            }}
          >
            <FaCalendarAlt /> Timeline
          </button>
          <button
            onClick={() => setActiveTab('stakeholder')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'stakeholder' ? '#007bff' : 'transparent',
              color: activeTab === 'stakeholder' ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'stakeholder' ? '600' : '400',
              borderRadius: activeTab === 'stakeholder' ? '4px 4px 0 0' : '0',
              marginRight: '4px',
              transition: 'all 0.2s',
              borderBottom: activeTab === 'stakeholder' ? '2px solid #007bff' : '2px solid transparent'
            }}
          >
            <FaUsers /> By Stakeholder
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'analytics' ? '#007bff' : 'transparent',
              color: activeTab === 'analytics' ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'analytics' ? '600' : '400',
              borderRadius: activeTab === 'analytics' ? '4px 4px 0 0' : '0',
              marginRight: '4px',
              transition: 'all 0.2s',
              borderBottom: activeTab === 'analytics' ? '2px solid #007bff' : '2px solid transparent'
            }}
          >
            <FaChartLine /> Analytics
          </button>
        </div>
      </div>

      {/* Content Area - same pattern as other working tabs */}
      <div className="content-area">
        {activeTab === 'timeline' && (
          <div>{renderTimelineView()}</div>
        )}
        {activeTab === 'stakeholder' && (
          <div>{renderStakeholderView()}</div>
        )}
        {activeTab === 'analytics' && (
          <Card>
            <Card.Body>
              <h5>Communication Analytics</h5>
              <Row className="mt-4">
                <Col md={6}>
                  <h6>By Source</h6>
                  <div className="source-stats">
                    <div>Manual: {stats.bySource.manual}</div>
                    <div>Event: {stats.bySource.event}</div>
                    <div>Plan: {stats.bySource.plan}</div>
                    <div>Assessment: {stats.bySource.assessment}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <h6>Recent Activity</h6>
                  <p>Charts and analytics coming soon...</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </div>
    </Container>
  );
}

export default CommunicationDashboard;